import * as THREE from "three";
import { createScene, type SceneBundle, type BackdropId } from "./SceneSetup";
import { Backplate } from "./Backplate";
import { History } from "./History";
import { Design } from "../neon/Design";
import { NeonTube } from "../neon/NeonTube";
import { TEMPLATES } from "../neon/templates";
import { DEFAULT_LIGHTING, type LightingSettings } from "../neon/types";
import { ToolManager } from "../tools/ToolManager";
import type { ToolContext } from "../tools/Tool";
import { DrawTool } from "../tools/DrawTool";
import { BendTool } from "../tools/BendTool";
import { SelectTool } from "../tools/SelectTool";
import { ColorTool } from "../tools/ColorTool";
import { DeleteTool } from "../tools/DeleteTool";
import { OrbitTool } from "../tools/OrbitTool";
import { ImportTool } from "../tools/ImportTool";
import type { ImportPayload } from "../ui/ImportDialog";
import { UI } from "../ui/UI";
import { readFromHash, writeToHash } from "../share/serialize";
import { exportDesignPng } from "../share/exportPng";

// 1 scene unit ≈ 4 inches. Calibrated so the seeded "welcome" sign reads as
// a believable wall-sized piece (~16" × 12") rather than a bus advertisement.
const UNITS_PER_INCH = 1 / 4;
// Rough industry numbers: ~9W per linear meter of LED-neon, $0.15/kWh, ~6h/day.
const WATTS_PER_UNIT = 9 * (1 / UNITS_PER_INCH) * 0.0254;
const KWH_RATE = 0.15;
const HOURS_PER_DAY = 6;
const DAYS_PER_MONTH = 30;
const SELECTION_RING_PAD = 0.3;

export class App {
  private scene: SceneBundle;
  private design = new Design();
  private tools: ToolManager;
  private ui: UI;
  private history: History;
  private backplate: Backplate;
  private selection = new Set<NeonTube>();
  private selectionRings = new Map<NeonTube, THREE.Mesh>();
  private startTime = performance.now();

  constructor(canvas: HTMLCanvasElement) {
    this.scene = createScene(canvas);
    this.scene.scene.add(this.design.root);

    this.backplate = new Backplate(this.design);
    this.scene.scene.add(this.backplate.mesh);

    const ctx: ToolContext = {
      scene: this.scene.scene,
      camera: this.scene.camera,
      renderer: this.scene.renderer,
      controls: this.scene.controls,
      design: this.design,
      drawingPlane: this.scene.drawingPlane,
      drawingPlaneMesh: this.scene.drawingPlaneMesh,
      getBrushColor: () => this.ui.getBrushColor(),
      getBrushThickness: () => this.ui.getBrushThickness(),
      getBrushIntensity: () => this.ui.getBrushIntensity(),
      setSelection: (t) => this.setSelection(t ? [t] : []),
      toggleSelection: (t) => this.toggleSelection(t),
      getSelection: () => this.primarySelection(),
      setHint: (msg) => this.ui.setHint(msg),
      toast: (msg) => this.ui.toast(msg),
      notifyChange: () => this.design.touch(),
      openImport: () => this.ui.openImportDialog(),
    };

    this.tools = new ToolManager(ctx, canvas);
    this.tools.register(new DrawTool());
    this.tools.register(new BendTool());
    this.tools.register(new SelectTool());
    this.tools.register(new ColorTool());
    this.tools.register(new DeleteTool());
    this.tools.register(new OrbitTool());
    this.tools.register(new ImportTool());

    this.ui = new UI(this.design, this.tools, {
      onShare: () => this.shareCurrent(),
      onClear: () => {
        this.design.clear();
        this.selection.clear();
        this.refreshSelectionUi();
      },
      onNameChange: (name) => {
        this.design.name = name || "untitled sign";
      },
      onUndo: () => this.undo(),
      onRedo: () => this.redo(),
      onExport: () => this.exportPng(),
      onLoadTemplate: (id) => this.loadTemplate(id),
      onBackdropChange: (id) => this.setBackdrop(id),
      onLightingChange: (l) => this.setLighting(l),
      onLightingReset: () => this.setLighting({ ...DEFAULT_LIGHTING }),
      onApplyColor: (hex) => this.applyToSelection((t) => t.setColor(hex)),
      onApplyThickness: (v) => this.applyToSelection((t) => t.setThickness(v)),
      onApplyIntensity: (v) => this.applyToSelection((t) => t.setIntensity(v)),
      onApplyWarm: (warm) => this.applyToSelection((t) => t.setWarm(warm)),
      onImportContours: (p) => this.applyImportedContours(p),
      onImportDialogClosed: () => {
        // Step off the Import tool so pressing `i` or clicking the toolbar
        // button re-opens the modal next time. Otherwise ToolManager.activate
        // no-ops because "import" is still marked active.
        if (this.tools.getActive()?.id === "import") this.tools.activate("select");
      },
    });

    this.history = new History(this.design);
    this.design.subscribe(() => {
      this.refreshStatus();
      this.pruneSelectionRings();
    });
    // Undo/redo button state has to track *committed* history, not raw edits —
    // raw edits fire before the 220ms debounce lands a snapshot.
    this.history.subscribe(() => {
      this.ui.setUndoRedoState(this.history.canUndo(), this.history.canRedo());
    });
    this.ui.setUndoRedoState(this.history.canUndo(), this.history.canRedo());

    this.wireKeyboardShortcuts();
    this.loadFromUrlOrSeed();
    this.tools.activate("draw");
    this.ui.setBackdrop(this.design.backdrop as BackdropId);
    this.scene.setBackdrop(this.design.backdrop as BackdropId);
    this.ui.setLighting(this.design.lighting);
    this.scene.setLighting(this.design.lighting);
    this.refreshStatus();

    this.animate();
  }

  // ── Selection ─────────────────────────────────────────────────────

  private primarySelection(): NeonTube | null {
    return this.selection.values().next().value ?? null;
  }

  private setSelection(tubes: NeonTube[]): void {
    this.selection = new Set(tubes);
    this.refreshSelectionUi();
  }

  private toggleSelection(tube: NeonTube): void {
    if (this.selection.has(tube)) this.selection.delete(tube);
    else this.selection.add(tube);
    this.refreshSelectionUi();
  }

  private refreshSelectionUi(): void {
    this.ui.setSelection([...this.selection]);
    this.updateSelectionRings();
  }

  private applyToSelection(fn: (t: NeonTube) => void): void {
    if (!this.selection.size) return;
    for (const t of this.selection) fn(t);
    this.design.touch();
  }

  /** Drop rings whose tubes were removed from the design. */
  private pruneSelectionRings(): void {
    const present = new Set(this.design.getTubes());
    let changed = false;
    for (const tube of [...this.selection]) {
      if (!present.has(tube)) {
        this.selection.delete(tube);
        changed = true;
      }
    }
    if (changed) this.refreshSelectionUi();
    else this.updateSelectionRings();
  }

  private updateSelectionRings(): void {
    // Remove rings for tubes no longer selected.
    for (const [tube, ring] of this.selectionRings) {
      if (!this.selection.has(tube)) {
        this.scene.scene.remove(ring);
        ring.geometry.dispose();
        (ring.material as THREE.Material).dispose();
        this.selectionRings.delete(tube);
      }
    }
    // Add or update rings for currently selected tubes.
    for (const tube of this.selection) {
      if (!tube.renderable) continue;
      let ring = this.selectionRings.get(tube);
      if (!ring) {
        ring = makeSelectionRing();
        this.scene.scene.add(ring);
        this.selectionRings.set(tube, ring);
      }
      fitRingToTube(ring, tube);
    }
  }

  // ── Actions ───────────────────────────────────────────────────────

  private duplicateSelection(): void {
    if (!this.selection.size) return;
    const offset = new THREE.Vector3(0.5, -0.5, 0);
    const newSet = new Set<NeonTube>();
    for (const t of this.selection) {
      const data = t.toData();
      const copy = new NeonTube({
        ...data,
        id: undefined,
        points: data.points.map(([x, y, z]) => [x + offset.x, y + offset.y, z + offset.z]),
      });
      copy.rebuildMesh();
      this.design.add(copy);
      newSet.add(copy);
    }
    this.selection = newSet;
    this.refreshSelectionUi();
    this.ui.toast(newSet.size === 1 ? "Tube duplicated" : `${newSet.size} tubes duplicated`);
  }

  private deleteSelection(): void {
    if (!this.selection.size) return;
    const count = this.selection.size;
    for (const t of this.selection) this.design.remove(t);
    this.selection.clear();
    this.refreshSelectionUi();
    this.ui.toast(count === 1 ? "Tube removed" : `${count} tubes removed`);
  }

  private undo(): void {
    if (this.history.undo()) this.syncSceneFromDesign();
  }

  private redo(): void {
    if (this.history.redo()) this.syncSceneFromDesign();
  }

  /** Push every persisted design-level setting back into UI + scene. */
  private syncSceneFromDesign(): void {
    this.selection.clear();
    this.refreshSelectionUi();
    this.ui.setName(this.design.name);
    this.ui.setBackdrop(this.design.backdrop as BackdropId);
    this.scene.setBackdrop(this.design.backdrop as BackdropId);
    this.ui.setLighting(this.design.lighting);
    this.scene.setLighting(this.design.lighting);
  }

  private async exportPng(): Promise<void> {
    try {
      // Hide selection rings during the export so they don't leak into the file.
      const rings = [...this.selectionRings.values()];
      rings.forEach((r) => (r.visible = false));
      try {
        await exportDesignPng(this.scene.composer, this.scene.renderer, this.design.name);
      } finally {
        rings.forEach((r) => (r.visible = true));
        this.scene.resize();
      }
      this.ui.toast("PNG saved to downloads");
    } catch {
      this.ui.toast("Export failed");
    }
  }

  private loadTemplate(id: string): void {
    const tpl = TEMPLATES.find((t) => t.id === id);
    if (!tpl) return;
    this.history.commit();
    this.design.loadData(tpl.build());
    this.syncSceneFromDesign();
  }

  private applyImportedContours(payload: ImportPayload): void {
    if (payload.contours.length === 0) return;
    this.history.commit();
    if (payload.replace) {
      this.design.clear();
      this.selection.clear();
      this.refreshSelectionUi();
    }
    const thickness = this.ui.getBrushThickness();
    const intensity = this.ui.getBrushIntensity();
    const warm = this.ui.getBrushWarm();
    for (const points of payload.contours) {
      const tube = new NeonTube({
        color: payload.color,
        thickness,
        intensity,
        warm,
        points,
      });
      tube.rebuildMesh();
      this.design.add(tube);
    }
    const n = payload.contours.length;
    this.ui.toast(`Imported ${n} tube${n === 1 ? "" : "s"}`);
    this.tools.activate("select");
  }

  private setBackdrop(id: BackdropId): void {
    this.scene.setBackdrop(id);
    this.design.backdrop = id;
    this.design.touch();
  }

  private setLighting(lighting: LightingSettings): void {
    this.design.lighting = { ...lighting };
    this.scene.setLighting(this.design.lighting);
    this.ui.setLighting(this.design.lighting);
    this.design.touch();
  }

  private shareCurrent(): void {
    const url = writeToHash(this.design.toData());
    navigator.clipboard?.writeText(url).then(
      () => this.ui.toast("Link copied to clipboard"),
      () => this.ui.toast("URL updated — copy from the address bar"),
    );
  }

  // ── Status (dimensions + power) ──────────────────────────────────

  private refreshStatus(): void {
    if (this.design.tubeCount === 0) {
      this.ui.setStatus(null);
      return;
    }
    const box = this.design.boundingBox();
    const size = new THREE.Vector3();
    box.getSize(size);
    const widthIn = size.x / UNITS_PER_INCH;
    const heightIn = size.y / UNITS_PER_INCH;
    const watts = this.design.totalLength() * WATTS_PER_UNIT;
    const monthlyCost = (watts / 1000) * KWH_RATE * HOURS_PER_DAY * DAYS_PER_MONTH;
    this.ui.setStatus({ widthIn, heightIn, watts, monthlyCost });
  }

  // ── Lifecycle / loading ──────────────────────────────────────────

  private loadFromUrlOrSeed(): void {
    const fromHash = readFromHash();
    if (fromHash) {
      this.design.loadData(fromHash);
      this.ui.setName(this.design.name);
      // syncSceneFromDesign would also push backdrop/lighting, but the
      // constructor's post-load block already does that — avoid double work.
      return;
    }
    this.seedExample();
  }

  private seedExample(): void {
    const greeting = new NeonTube({ color: "#ff2bd6", thickness: 0.14, intensity: 1.5 });
    const pts: [number, number, number][] = [
      [-2.2, 0.8, 0],
      [-2.0, 1.4, 0],
      [-1.2, 1.4, 0],
      [-1.0, 0.8, 0],
      [-1.0, 0.2, 0],
      [-1.6, 0.0, 0],
      [-2.2, 0.0, 0],
    ];
    for (const p of pts) greeting.addPoint(new THREE.Vector3(...p));
    this.design.add(greeting);

    const swoosh = new NeonTube({ color: "#2affea", thickness: 0.12, intensity: 1.8 });
    for (let i = 0; i <= 24; i++) {
      const t = i / 24;
      const x = -0.2 + t * 3.2;
      const y = Math.sin(t * Math.PI * 1.4) * 0.9 - 0.2;
      swoosh.addPoint(new THREE.Vector3(x, y, 0));
    }
    this.design.add(swoosh);
    this.design.name = "welcome";
    this.ui.setName(this.design.name);
  }

  // ── Keyboard ─────────────────────────────────────────────────────

  private wireKeyboardShortcuts(): void {
    const tools: Record<string, string> = {
      d: "draw",
      b: "bend",
      v: "select",
      p: "color",
      x: "delete",
      o: "orbit",
      i: "import",
    };
    window.addEventListener("keydown", (ev) => {
      const target = ev.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;

      const cmd = ev.metaKey || ev.ctrlKey;
      const key = ev.key.toLowerCase();

      if (cmd && key === "z" && !ev.shiftKey) {
        ev.preventDefault();
        this.undo();
        return;
      }
      if (cmd && (key === "y" || (key === "z" && ev.shiftKey))) {
        ev.preventDefault();
        this.redo();
        return;
      }
      if (cmd && key === "d") {
        ev.preventDefault();
        this.duplicateSelection();
        return;
      }
      if (cmd && key === "e") {
        ev.preventDefault();
        this.exportPng();
        return;
      }
      if ((key === "delete" || key === "backspace") && this.selection.size && this.tools.getActive()?.id !== "bend") {
        ev.preventDefault();
        this.deleteSelection();
        return;
      }

      const id = tools[key];
      if (id) this.tools.activate(id);
    });
  }

  private animate = (): void => {
    requestAnimationFrame(this.animate);
    const t = performance.now() - this.startTime;
    this.scene.controls.update();
    this.design.tickAll(t);
    if (this.selection.size) this.updateSelectionRings();
    this.scene.composer.render();
  };
}

function makeSelectionRing(): THREE.Mesh {
  const mat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    wireframe: true,
    transparent: true,
    opacity: 0.25,
    depthTest: false,
    toneMapped: false,
  });
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 0.05), mat);
  mesh.renderOrder = 998;
  return mesh;
}

function fitRingToTube(ring: THREE.Mesh, tube: NeonTube): void {
  const box = new THREE.Box3().setFromPoints(tube.points);
  box.expandByScalar(SELECTION_RING_PAD);
  const size = new THREE.Vector3();
  box.getSize(size);
  const center = new THREE.Vector3();
  box.getCenter(center);
  ring.geometry.dispose();
  ring.geometry = new THREE.BoxGeometry(size.x, size.y, Math.max(size.z, 0.05));
  ring.position.copy(center);
}
